import { Building2, Home, ShieldCheck, UserCog } from 'lucide-react';
import type { CompanyResponse, UserResponse } from '@/shared/api';
import { roleLabel, userRole } from '../userStatus';

interface Props {
  user: UserResponse;
  company: CompanyResponse | null;
  assignedCount: number;
}

/**
 * Faktenband der Benutzerakte — eine umrahmte Grid-Surface mit echten Dividern (§6), gleiche
 * Ausprägung wie `BookingFacts`.
 *
 * <p>Es zeigt genau die vier Werte, an denen ein Administrator entscheidet, ob er hier etwas
 * tun muss: was der Benutzer darf, wem er zugeordnet ist, wie viele Apartments er sieht und ob
 * sein Zugang zweifach abgesichert ist.</p>
 */
export function UserDetailFacts({ user, company, assignedCount }: Props) {
  const facts = [
    {
      icon: UserCog,
      label: roleLabel(userRole(user)),
      detail: 'Rolle im Workspace',
      accent: 'text-postmark',
    },
    {
      icon: Building2,
      label: company?.name ?? 'Keiner Firma zugeordnet',
      detail: 'Firma',
      accent: 'text-sage',
    },
    {
      icon: Home,
      label:
        assignedCount === 0
          ? 'Keine direkte Zuweisung'
          : `${assignedCount} ${assignedCount === 1 ? 'Apartment' : 'Apartments'}`,
      detail: 'Direkt zugewiesen',
      accent: 'text-postmark',
    },
    {
      icon: ShieldCheck,
      label: user.twoFactorEnabled ? 'Zwei-Faktor aktiv' : 'Zwei-Faktor fehlt',
      detail: 'Anmeldesicherheit',
      accent: user.twoFactorEnabled ? 'text-sage' : 'text-amber',
    },
  ];

  return (
    <dl className="border-line bg-card shadow-card grid rounded-lg border sm:grid-cols-2 xl:grid-cols-4">
      {facts.map(({ icon: Icon, label, detail, accent }) => (
        <div
          key={detail}
          className="border-line/60 flex min-w-0 gap-3 border-b p-4 last:border-b-0 sm:border-r xl:border-b-0"
        >
          <Icon className={`${accent} mt-0.5 shrink-0`} size={22} strokeWidth={1.8} aria-hidden />
          <div className="min-w-0">
            <dt className="text-ink wrap-break-word font-semibold">{label}</dt>
            <dd className="text-ink-soft mt-0.5 text-xs">{detail}</dd>
          </div>
        </div>
      ))}
    </dl>
  );
}
